import { Input, Translation, useIsMounted } from '@ps/web-shared-components';
import type { SelectOption } from '@ps/web-shared-components';
import { required } from '@ps/web-shared-validators';
import React, { useCallback, useEffect, useState } from 'react';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import { Country } from '@swf/core/enums';
import { select } from '@swf/core/store';

import { State } from '../../../../../../types/frenchState';
import { getCountryStates } from '../../../../../../utils/country-states/country-states';
import {
  countriesWithoutFrenchTerritories as countries,
  frenchTerritories,
  getFrenchCitiesByState
} from '../../../../../../utils/placeOfBirth';
import City from '../../../../../City/City';
import { findState } from '../../../../../State/State';
import RMFDropDown from '../../../RMFDropDown/RMFDropDown';

import {
  TXTCLI_City_of_Birth,
  TXTCLI_Country_of_Birth,
  TXTCLI_Please_select_your_department__ELL,
  TXTWCL_Department_of_birth,
  TXTWCL_Please_enter_a_valid_cityX,
  TXTWCL_Please_select_your_city_of_bir_ELL,
  TXTWCL_Select_city_of_birth,
  TXTWCL_Select_country_of_birth,
  TXTWCL_Select_department_of_birth
} from './PlaceOfBirthFieldsFR.messages.json';
import { city, wrapper } from './PlaceOfBirthFieldsFR.scss';

const frenchStatesWithNoCities = [
  State.AntarcticTerritories,
  State.ClippertonIsland,
  State.FrenchPolynesia,
  State.NewCaledonia,
  State.SaintBarthelemy,
  State.SaintMartin,
  State.SaintPierre,
  State.WallisAndFutuna
];

const frenchTerritoriesWithDepartment = ['PF', 'NC', 'WF', 'BL', 'MF', 'PM', 'TF', 'CP'];

const countryOfBirthField = 'countryOfBirth';
const stateOfBirthField = 'stateOfBirth';
const cityOfBirthField = 'cityOfBirth';
const departmentOfBirthField = 'departmentOfBirthField';

const PlaceOfBirthFieldsFR = () => {
  const form = useForm();
  const country = useSelector(select.account.getCountry) as Country;
  const [cityOptions, setCityOptions] = useState<SelectOption[]>([]);
  const initialCountry = frenchTerritories.includes(country) ? Country.France : country;
  const isMounted = useIsMounted();

  const {
    input: { value: countryOfBirth }
  } = useField(countryOfBirthField, { initialValue: initialCountry });
  const {
    input: { value: stateOfBirth }
  } = useField(stateOfBirthField);

  const countryFrance = countryOfBirth === Country.France;
  const selectedCountry = countries.find((opt) => opt.value === countryOfBirth);

  const stateOptions = (
    getCountryStates(countryOfBirth) as { value: State; text: string }[]
  ).filter((state) => !frenchStatesWithNoCities.includes(state?.value));

  const resetCityState = () => {
    form.change(cityOfBirthField);
    form.resetFieldState(cityOfBirthField);
    form.change(departmentOfBirthField, undefined);
  };

  const setFrenchCities = useCallback(async () => {
    const frenchCitiesByState = await getFrenchCitiesByState(
      findState(stateOfBirth, stateOptions)?.value ?? ''
    );
    isMounted() && setCityOptions(frenchCitiesByState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateOfBirth]);

  useEffect(() => {
    isMounted() && setFrenchCities();
  }, [setFrenchCities, isMounted]);

  return (
    <div className={wrapper}>
      <RMFDropDown
        id={countryOfBirthField}
        label={TXTCLI_Country_of_Birth}
        placeholder={<Translation {...TXTWCL_Select_country_of_birth} />}
        options={countries}
        onSelect={resetCityState}
      />
      {countryFrance && (
        <RMFDropDown
          id={stateOfBirthField}
          label={TXTWCL_Department_of_birth}
          placeholder={<Translation {...TXTWCL_Select_department_of_birth} />}
          options={stateOptions}
          onSelect={resetCityState}
          validators={[required(TXTCLI_Please_select_your_department__ELL)]}
        />
      )}
      {countryFrance ? (
        <RMFDropDown
          id={cityOfBirthField}
          label={TXTCLI_City_of_Birth}
          placeholder={<Translation {...TXTWCL_Select_city_of_birth} />}
          options={cityOptions}
          validators={[required(TXTWCL_Please_select_your_city_of_bir_ELL)]}
          disabled={!cityOptions?.length}
        />
      ) : (
        <>
          {frenchTerritoriesWithDepartment.includes(countryOfBirth) && (
            <Input
              id={departmentOfBirthField}
              label={TXTWCL_Department_of_birth}
              disabled
              value={selectedCountry?.text}
            />
          )}
          <City
            fieldName={cityOfBirthField}
            requiredMessage={TXTWCL_Please_enter_a_valid_cityX}
            label={TXTCLI_City_of_Birth}
            className={city}
          />
        </>
      )}
    </div>
  );
};

export default PlaceOfBirthFieldsFR;
